import type { ArtifactListingEntry } from '@crowdex/agent-contracts/api';
import type { PanelView } from '@crowdex/host-kit/host';
import type { ToolRef } from '@crowdex/plugin-contracts';
import type { WorkspacePort } from '@crowdex/workspace-protocol';
import type { ComponentProps, ReactNode } from 'react';
import { SummaryArtifactsSection } from '@/components/desktop-summary/summary-artifacts-section';
import {
  SummaryEnvironmentSection,
  type SummaryHandoff,
  type SummaryPlace,
  type SummaryWorkspaceTools,
} from '@/components/desktop-summary/summary-environment-section';
import { SummaryPictureInPictureSection } from '@/components/desktop-summary/summary-picture-in-picture-section';
import {
  SummaryAssistantSection,
  SummaryItemsSection,
  type SummaryRefTargets,
  SummaryServicesSection,
  type SummaryTask,
  SummaryTasksSection,
} from '@/components/desktop-summary/summary-refs-sections';
import { SummarySourcesSection } from '@/components/desktop-summary/summary-sources-section';
import { SummarySubscriptionsSection } from '@/components/desktop-summary/summary-subscriptions-section';
import {
  type SummaryGitControls,
  SummaryWorkspaceSection,
} from '@/components/desktop-summary/summary-workspace-section';
import type { NativeGitStatus } from '@/lib/native-desktop';
import type { ConversationPullRequestClaim } from '@/lib/pull-request';

type SummaryContentProps = {
  /** The conversation's folder; `null` (or the personal workspace) has no Code section. */
  workspace: string | null;
  /** The folder is a repository the conversation works in (not the personal workspace). */
  repository: boolean;
  gitStatus: NativeGitStatus | null;
  place: SummaryPlace;
  /** The cloud sandbox is up. */
  running: boolean;
  servers: readonly WorkspacePort[];
  artifacts?: readonly ArtifactListingEntry[];
  onOpenArtifact?: (artifact: ArtifactListingEntry) => void;
  /** What its tools touched (`conversationRefs`): artifacts, app items, services' results, tasks. */
  refs?: readonly ToolRef[];
  /** The artifacts it can see, where a ref finds its own. */
  artifactIndex?: readonly ArtifactListingEntry[];
  /** How a ref shows and opens (`useRefTargets`), as its preview in the transcript. */
  refTargets?: SummaryRefTargets;
  /** Its tasks and runs, with their state. */
  tasks?: readonly SummaryTask[];
  onOpenTask?: (task: SummaryTask) => void;
  /** Its assistant and what started it, at the top. */
  assistant?: ComponentProps<typeof SummaryAssistantSection>;
  sources: readonly { label: string; url: string }[];
  /** Opens a tool of the panel (Review, Pull request, Ports). */
  onOpenPanel: (panel: PanelView) => void;
  taskId: string;
  preview?: boolean;
  hidden: boolean;
  /** The branch's chip with its actions (switch, create), in the Code section. */
  branchControl?: ReactNode;
  /** Commit, push, create a pull request (the Code section's actions). */
  git?: Omit<SummaryGitControls, 'gitStatus' | 'cloud'>;
  /** The workspace's actions and editor (Runs in, on this computer). */
  tools?: SummaryWorkspaceTools;
  /** Where the conversation can go (Runs in): the handoff. */
  handoff?: SummaryHandoff;
  /** Its pull request in a folder other conversations share (`PullRequestSessionContext`). */
  pullRequestClaim?: ConversationPullRequestClaim | null | undefined;
};

/**
 * The lines the Code section counts: those of the conversation's files when it is a
 * conversation's (other conversations may be working in the folder), else the folder's.
 */
export function summaryChanges(
  gitStatus: NativeGitStatus | null,
  conversationFiles: { own: readonly string[]; shared: readonly string[] } | null | undefined,
): { additions: number; deletions: number } {
  if (gitStatus === null) return { additions: 0, deletions: 0 };
  if (conversationFiles === null || conversationFiles === undefined)
    return { additions: gitStatus.additions, deletions: gitStatus.deletions };
  const paths = new Set([...conversationFiles.own, ...conversationFiles.shared]);
  let additions = 0;
  let deletions = 0;
  for (const file of gitStatus.fileStats ?? []) {
    if (!paths.has(file.path)) continue;
    additions += file.additions;
    deletions += file.deletions;
  }
  return { additions, deletions };
}

/**
 * The conversation's Summary (D5, D5b, mobile 36; `brand/crow-extensions.html` "Le résumé"), in
 * this order: its Assistant (and what started it), its Code (repository, branch, changes, pull
 * request, and Commit…, Push, Create PR as they apply), its Runs in (where it runs, its servers,
 * its actions and editor, and the handoff), its Artifacts (made, changed, read), its App items,
 * its Services' results, its Tasks and runs, its Sources, what wakes its agent and Computer Use.
 * Every section folds by its chevron; past three rows, "See all · N". Its rows open the panel's
 * tools, an item docked beside the conversation, or a service's page ↗.
 */
export function SummaryContent({
  workspace,
  repository,
  gitStatus,
  place,
  running,
  servers,
  artifacts = [],
  onOpenArtifact,
  refs = [],
  artifactIndex,
  refTargets,
  tasks = [],
  onOpenTask,
  assistant,
  sources,
  onOpenPanel,
  taskId,
  preview,
  hidden,
  branchControl,
  git,
  tools,
  handoff,
  pullRequestClaim,
}: SummaryContentProps) {
  const changes = summaryChanges(gitStatus, git?.conversationFiles);
  return (
    <div className="@container flex min-h-full min-w-0 flex-col gap-4 overflow-x-hidden">
      <h2 className="sr-only">Summary</h2>
      {assistant !== undefined && <SummaryAssistantSection {...assistant} />}
      {repository && workspace !== null && (
        <SummaryWorkspaceSection
          additions={changes.additions}
          branch={gitStatus?.branch ?? null}
          branchControl={branchControl}
          deletions={changes.deletions}
          git={git === undefined ? undefined : { ...git, gitStatus, cloud: place === 'cloud' }}
          onOpenChanges={() => onOpenPanel('review')}
          onOpenPullRequest={() => onOpenPanel('pull-request')}
          pullRequest={{
            taskId,
            workspace,
            cloud: place === 'cloud',
            visible: !hidden,
            preview: preview === true,
            gitStatus,
            claim: pullRequestClaim,
          }}
          remoteUrl={gitStatus?.remoteUrl ?? null}
          workspace={workspace}
        />
      )}

      <SummaryEnvironmentSection
        handoff={handoff}
        onOpenPorts={() => onOpenPanel('ports')}
        place={place}
        ports={servers}
        running={running}
        tools={tools}
        workspace={workspace}
      />

      {onOpenArtifact !== undefined && (
        <SummaryArtifactsSection
          artifacts={artifacts}
          index={artifactIndex}
          onOpen={onOpenArtifact}
          onOpenRef={refTargets?.open}
          refs={refs}
        />
      )}

      {refTargets !== undefined && <SummaryItemsSection refs={refs} targets={refTargets} />}
      {refTargets !== undefined && (
        <SummaryServicesSection
          branchPullRequestUrl={pullRequestClaim?.url}
          refs={refs}
          targets={refTargets}
        />
      )}
      {onOpenTask !== undefined && <SummaryTasksSection onOpen={onOpenTask} tasks={tasks} />}

      {(sources.length > 0 || !repository) && <SummarySourcesSection sources={sources} />}

      {/* The preview shows the boards' (D68): a project's task wakes on its PR. */}
      <SummarySubscriptionsSection conversation={taskId} visible={!hidden} />

      {preview !== true && <SummaryPictureInPictureSection conversation={taskId} />}
    </div>
  );
}
